import React, {useEffect, useMemo, useState} from 'react';
import {getApiService, useApi} from '@/services/api';
import {getUserFriendlyErrorMessage} from '@/utils/errorHandling';
import {
    Button,
    DeleteConfirmationPopup,
    HeadingSize,
    Label,
    LabelType,
    RowAction,
    Search,
    Table,
    TableColumn,
    useToast
} from '@fs/uikit-react';
import './Users.css';
import {Role} from '../Roles';
import UserFormModal from './UserFormModal';

interface User {
    id: string;
    username: string;
    email?: string;
    roles: [Role];
}

const UsersPage = () => {
    const [searchValue, setSearchValue] = useState('');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);

    const toast = useToast();

    const openCreateModal = () => {
        setIsCreateModalOpen(true);
    };
    const closeCreateModal = () => { setIsCreateModalOpen(false); };

    const openEditModal = (user: User) => {
        setSelectedUser(user);
        setIsEditModalOpen(true);
    };
    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedUser(null);
    };

    const openDeleteModal = (user: User, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent opening edit modal
        setUserToDelete(user);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setUserToDelete(null);
    };

    const deleteUser = async () => {
        if (!userToDelete) return;

        try {
            await apiService.client.delete(`/users/${userToDelete.id}`);
            refreshUsers(); // Refresh the user list
            closeDeleteModal();
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.showErrorToast(
                'Помилка видалення користувача',
                getUserFriendlyErrorMessage(error)
            );
        }
    };

    // Get API service inside the component to ensure it's initialized
    // Memoize the API service to prevent it from changing on each render
    const apiService = useMemo(() => getApiService(), []);
    // Memoize the API call function to prevent it from changing on each render
    const apiCallFunction = useMemo(() =>
            (client: { get: (arg0: string) => any; }) => client.get('/users'),
        []
    );

    const {
        data: users,
        loading,
        error,
        execute: refreshUsers,
    } = useApi<User[]>(
        apiService.client,
        apiCallFunction,
        apiService.loadingStateManager,
        'users-list'
    );

    useEffect(() => {
        if (error) {
            console.error('Error fetching users:', error);
        }
    }, [error]);

    // Filter users based on search value
    const filteredUsers = users?.filter(user =>
        searchValue === '' ||
        user.username.toLowerCase().includes(searchValue.toLowerCase())
    ) || [];

    // Define table columns
    const columns: TableColumn[] = [
        {
            key: 'username',
            title: "Ім'я користувача",
            width: '30%',
        },
        {
            key: 'roles',
            title: 'Роль',
            width: '30%',
        },
        {
            key: 'email',
            title: 'Email',
            width: '40%',
        }
    ];

    // Transform user data for the table
    const tableData = filteredUsers.map(user => ({
        id: user.id,
        username: user.username,
        roles: user.roles.map(role => role.name).join(', '),
        email: user.email || '-',
        // Keep the original user object for actions
        _original: user
    }));

    // Define row actions
    const rowActions: RowAction[] = [
        {
            type: 'delete',
            onClick: (record) => {
                // Create a synthetic event since we don't have the actual event
                const syntheticEvent = { stopPropagation: () => {} } as React.MouseEvent;
                openDeleteModal(record._original, syntheticEvent);
            }
        }
    ];

    return (
        <div className={"container"}>
            <div className={'title'}>
                <Label
                    text={'Користувачі'}
                    type={LabelType.HEADING}
                    headingSize={HeadingSize.L}
                />
                <Button
                  title="Створити користувача"
                  onClick={openCreateModal}
                />
            </div>

            <div>
                <div className="search-button-container">
                    <div className="search-container">
                        <Search
                            placeholder="Пошук користувачів"
                            value={searchValue}
                            onChange={setSearchValue}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="loading-state">
                        <Label text="Завантаження користувачів..." type={LabelType.PARAGRAPH} />
                    </div>
                ) : error ? (
                    <div className="error-state">
                        <Label 
                            text={`Помилка завантаження користувачів: ${getUserFriendlyErrorMessage(error)}`} 
                            type={LabelType.PARAGRAPH}
                        />
                    </div>
                ) : (
                    <Table
                        data={tableData}
                        columns={columns}
                        rowActions={rowActions}
                        onClick={(record) => { openEditModal(record._original); }}
                        className="users-table"
                        sticky={true}
                    />
                )}
            </div>

            {isCreateModalOpen &&
            <UserFormModal
                onClose={closeCreateModal}
                onUserSaved={refreshUsers}
            />}

            {isEditModalOpen &&
            <UserFormModal
                onClose={closeEditModal}
                onUserSaved={refreshUsers}
                user={selectedUser || undefined}
            />}

            {isDeleteModalOpen &&
                <DeleteConfirmationPopup
                    onClose={closeDeleteModal}
                    name={'користувача'}
                    confirmButtonText="Видалити"
                    onConfirmButtonClick={deleteUser}
                />
            }
        </div>
    );
};

export default UsersPage
